'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useQueryState } from 'nuqs';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner } from '@/lib/rbac';

/** URL key the Owner's Agent Insights team choice lives under (`?team=`). */
export const AGENT_INSIGHTS_TEAM_PARAM = 'team';

/**
 * The single team an Owner scoped Agent Insights to, or null for org-wide.
 *
 * Provided by the Agent Insights page and read by the data hooks, so every
 * tab's queries share one key element and one `team_uuid` request param
 * without threading a prop through every section. Outside that page there is
 * no provider, so callers (Home, member detail) resolve to null and stay
 * org-wide. Only an Owner gets a value: an Admin's team is forced by the
 * backend and a Member is unchanged.
 */
const AgentInsightsTeamContext = createContext<string | null>(null);

/** Selected team for the current Owner as `[teamUuid, setTeamUuid]`; the URL is the source of truth. */
export function useSelectedAgentInsightsTeam() {
  const { user } = useUserLogged();
  const [team, setTeam] = useQueryState(AGENT_INSIGHTS_TEAM_PARAM);
  const teamUuid = isOwner(user?.Role) && team ? team : null;
  return [teamUuid, setTeam] as const;
}

export function AgentInsightsTeamProvider({
  teamUuid,
  children,
}: {
  teamUuid: string | null;
  children: ReactNode;
}) {
  return (
    <AgentInsightsTeamContext.Provider value={teamUuid}>
      {children}
    </AgentInsightsTeamContext.Provider>
  );
}

/** Team to scope Agent Insights requests to, or null for org-wide. */
export function useAgentInsightsTeamUuid(): string | null {
  return useContext(AgentInsightsTeamContext);
}
